import _ from "lodash";

import {
  getDeleteCostSave,
  getOrphanScheduleItems,
  idKey,
  isSameCost,
  num,
  scheduleItemValue,
  scheduleTotal,
  toScheduleItem,
} from "./budgetMasterRows.js";
import { budgetLineItemLabel, budgetLineItemStoredName } from "./budgetLineItems.js";
import { groupClonedScheduleItems } from "./cloneBudgetSchedule.js";

// Pure planning for Budget Master's bulk edits: one BudgetMaster_Update save and a before/after preview per target.
// Keep JSX, the toolkit, Vite aliases, and helpers.js out so node can run the self-check.

/**
 * Copy to…: each target's `cost` ({ category, name }) becomes the source's: its $/acre, and copies of its schedule
 * items in place of the target's. A target without the cost gets it, spelled like the source's. Source and targets are
 * buildBudgetMasterRows rows; the preview has each target's labels and $/acre before (null without the cost) and after.
 */
export function planCopyCost(source, targets, cost, newId) {
  const sourceCost = source.lineItems.find((li) => isSameCost(li, cost));
  const value = num(sourceCost.value);
  const plans = targets
    .filter((target) => idKey(target.id) !== idKey(source.id))
    .map((target) => {
      const targetCost = target.lineItems.find((li) => isSameCost(li, cost));
      const { category, name: rawName } = targetCost ?? sourceCost;
      const name = budgetLineItemStoredName(rawName);
      const copies =
        groupClonedScheduleItems(sourceCost.scheduleItems, new Map([[idKey(source.id), target.id]]), newId)[0] ?? [];
      return {
        lineItem: {
          id: targetCost?.id ?? newId(),
          budget_county_practice_id: target.id,
          category,
          name,
          value,
          is_active: true,
        },
        scheduleItems: [
          ...(targetCost
            ? getDeleteCostSave(targetCost).BudgetCountyPracticeScheduleItems
            : getOrphanScheduleItems(target, cost)),
          ...copies.map((copy) => ({ ...copy, category, line_item_name: name })),
        ],
        preview: {
          ..._.pick(target, ["id", "budget_name", "countyLabel", "cropLabel"]),
          before: targetCost ? num(targetCost.value) : null,
          after: value,
        },
      };
    });
  return {
    save: {
      BudgetCountyPracticeLineItems: plans.map((plan) => plan.lineItem),
      BudgetCountyPracticeScheduleItems: plans.flatMap((plan) => plan.scheduleItems),
    },
    preview: plans.map((plan) => plan.preview),
  };
}

/**
 * Copy all costs to…: each target ends with exactly the source's costs. Each cost is copied as Copy to… copies it, and
 * every other cost on the target is removed with its schedule items. Source and targets are buildBudgetMasterRows rows;
 * the preview has each target's labels, its cost/ac before and after, and the names of the costs it loses.
 */
export function planCopyAllCosts(source, targets, newId) {
  // The county/crop page lets a cost be added twice: it is copied once, and a target's second one is removed.
  const sourceCosts = _.uniqWith(source.lineItems, isSameCost);
  const copies = sourceCosts.map((cost) => planCopyCost(source, targets, cost, newId).save);
  const copiedPerAcre = _.sumBy(sourceCosts, (li) => num(li.value));
  const copiedIds = new Set(copies.flatMap((copy) => copy.BudgetCountyPracticeLineItems.map((li) => idKey(li.id))));
  const plans = targets
    .filter((target) => idKey(target.id) !== idKey(source.id))
    .map((target) => {
      const removed = target.lineItems.filter((li) => !copiedIds.has(idKey(li.id)));
      return {
        removals: removed.map(getDeleteCostSave),
        preview: {
          ..._.pick(target, ["id", "budget_name", "countyLabel", "cropLabel"]),
          before: target.costPerAcre,
          after: roundTo(copiedPerAcre, 2),
          removed: removed.map((li) => budgetLineItemLabel(li.name)),
        },
      };
    });
  const saves = [...copies, ...plans.flatMap((plan) => plan.removals)];
  return {
    save: {
      BudgetCountyPracticeLineItems: saves.flatMap((save) => save.BudgetCountyPracticeLineItems),
      BudgetCountyPracticeScheduleItems: saves.flatMap((save) => save.BudgetCountyPracticeScheduleItems),
    },
    preview: plans.map((plan) => plan.preview),
  };
}

const roundTo = (value, digits) => Number(value.toFixed(digits));

/**
 * Adjust prices…: `target` "cost" is `cost`'s $/acre on rows without schedule items, and { itemName } is the unit
 * price of the cost's items with that name, in any case. `op` "set" makes it `amount`, and "pct" changes it by
 * `amount`%. A unit price is rounded to 4 decimals like the schedule proc, and a $/acre changed by % to the cent. Set
 * creates the cost, spelled like `cost`, where a row lacks it; % skips that row. Rows left as they were are left out
 * of the save and of the preview, which is planCopyCost's with a ≡ cost's new schedule total.
 */
export function planAdjustPrices(rows, { target, op, amount }, cost, newId) {
  const byPct = (value) => num(value) * (1 + amount / 100);
  const plans = rows.flatMap((row) => {
    const lineItem = row.lineItems.find((li) => isSameCost(li, cost));
    const labels = _.pick(row, ["id", "budget_name", "countyLabel", "cropLabel"]);

    if (target === "cost") {
      if (lineItem ? lineItem.isScheduled : op === "pct") return [];
      const value = op === "set" ? amount : roundTo(byPct(lineItem.value), 2);
      if (lineItem && value === num(lineItem.value)) return [];
      const { category, name: rawName } = lineItem ?? cost;
      const name = budgetLineItemStoredName(rawName);
      return [
        {
          lineItems: [
            {
              id: lineItem?.id ?? newId(),
              budget_county_practice_id: row.id,
              category,
              name,
              value,
              is_active: true,
            },
          ],
          scheduleItems: getOrphanScheduleItems(row, cost),
          preview: { ...labels, before: lineItem ? num(lineItem.value) : null, after: value },
        },
      ];
    }

    const isItem = (si) => si.name?.toLowerCase() === target.itemName.toLowerCase();
    const scheduleItems = lineItem?.scheduleItems ?? [];
    const adjusted = scheduleItems.map((si) =>
      isItem(si) ? { ...si, unit_value: roundTo(op === "set" ? amount : byPct(si.unit_value), 4) } : si
    );
    const changed = adjusted.filter((si, index) => num(si.unit_value) !== num(scheduleItems[index].unit_value));
    if (!changed.length) return [];
    return [
      {
        lineItems: [],
        scheduleItems: changed.map((si) => ({ ...toScheduleItem(si), value: scheduleItemValue(si) })),
        preview: { ...labels, before: num(lineItem.value), after: scheduleTotal(adjusted) },
      },
    ];
  });
  return {
    save: {
      BudgetCountyPracticeLineItems: plans.flatMap((plan) => plan.lineItems),
      BudgetCountyPracticeScheduleItems: plans.flatMap((plan) => plan.scheduleItems),
    },
    preview: plans.map((plan) => plan.preview),
  };
}
